/**
 * Steelman cards (one template for all eight countries) and cabinet role guides.
 *
 * Template: "In our own words" (limited to the five game axes; no culture-war topics on any card), "Our real
 * strengths", "What our critics say", and a lens with 2 principles the tradition affirms and 2 it presses on
 * the country, all drawn from one shared list. The lens is shown to students only at the debrief.
 */
import type { CountryId, RoleId, SteelmanCard } from '../../shared/types.ts';

/** The one shared list of principles every lens draws from. Stated in general terms, without ranking. */
export const LENS_PRINCIPLES = {
  dignity: 'The dignity of every person',
  common_good: 'The common good: conditions that let everyone flourish',
  subsidiarity: 'Subsidiarity: decisions made at the most local level that works',
  solidarity: 'Solidarity: we are responsible for one another, near and far',
  poor: 'Care for the poor and vulnerable first',
  workers: 'The rights of workers to fair pay and safe work',
  creation: 'Care for creation and for future generations',
  family: 'The family as the first community',
  participation: 'The right of people to take part in public life',
  religious_freedom: 'Freedom of conscience and belief',
  goods: 'The goods of the earth are meant for everyone',
  initiative: 'The right to private property and private initiative',
  homeland: 'Love of one’s homeland and its culture',
  welcome: 'Welcome for migrants and refugees',
  peace: 'Peace and the careful use of force',
} as const;
export type LensPrincipleId = keyof typeof LENS_PRINCIPLES;

interface CardSource {
  inOwnWords: string;
  strengths: string[];
  critics: string[];
  affirms: [LensPrincipleId, LensPrincipleId];
  challenges: [LensPrincipleId, LensPrincipleId];
}

const SOURCES: Record<CountryId, CardSource> = {
  ironvale: {
    inOwnWords:
      'We believe free people and free businesses build the strongest country. Low taxes let families keep what they earn. Cheap, reliable energy powers good jobs. We trade fairly and use tariffs to win better deals. We keep the peace by being strong, and we expect our allies to do their part.',
    strengths: [
      'Growth: low taxes and light rules help businesses start and hire.',
      'Energy: we make our own energy and sell it to our friends.',
      'A strong defense keeps attackers away.',
      'Local control: states and towns decide what works for them.',
    ],
    critics: [
      'Tax cuts can grow the debt if the economy does not grow fast enough.',
      'Tariffs can raise prices and upset allies.',
      'A thinner safety net can leave some families behind in a crisis.',
      'Relying on fossil fuels adds to warming.',
    ],
    affirms: ['subsidiarity', 'family'],
    challenges: ['creation', 'solidarity'],
  },
  brightwater: {
    inOwnWords:
      'We believe everyone deserves a fair shot. A regulated market, fair wages and strong unions grow the economy from the middle out. A safety net catches people when times are hard. We work with allies and world institutions, and we invest in clean energy for the next generation.',
    strengths: [
      'A strong safety net helps families with health care and food.',
      'Alliances make every partner safer and stronger.',
      'Clean energy creates new jobs and cuts pollution.',
      'Independent courts and a professional civil service protect rights.',
    ],
    critics: [
      'High spending can lead to debt and higher taxes.',
      'Many rules can slow down businesses and building.',
      'Decisions take longer when many people must agree.',
      'A smaller military may invite challenges.',
    ],
    affirms: ['solidarity', 'workers'],
    challenges: ['subsidiarity', 'initiative'],
  },
  meridia: {
    inOwnWords:
      'We believe each person owns their own life and should be free to choose. Free markets and free trade create wealth. Government should be a fair referee, not a boss. Charities, clubs and neighbors solve problems best when they choose to help. We want peace and trade with every nation.',
    strengths: [
      'Free trade makes goods cheaper and builds friendships.',
      'Low taxes and sound money reward hard work and saving.',
      'Voluntary groups respond fast and know their neighbors.',
      'Staying out of alliances keeps us out of other nations’ wars.',
    ],
    critics: [
      'Some problems, like pandemics and pollution, are hard to solve by choice alone.',
      'Without stockpiles, a crisis can hit hard.',
      'Without allies, a small nation may stand alone.',
      'Markets can leave the poorest behind.',
    ],
    affirms: ['dignity', 'initiative'],
    challenges: ['common_good', 'goods'],
  },
  kestran: {
    inOwnWords:
      'We believe a strong, steady government can plan for the long run. Our party sets five-year plans, builds roads and factories, and works to lift every family out of poverty. Markets and private firms help us grow, but the state guides the big choices. We keep order at home and stay out of other nations’ affairs.',
    strengths: [
      'Long-range planning builds big projects fast.',
      'Poverty reduction: millions of families have a better life.',
      'Stability: steady rules help businesses plan ahead.',
      'Green industry: the state can move quickly on clean energy.',
    ],
    critics: [
      'People cannot vote the party out of power.',
      'Free speech, a free press and freedom of belief are limited, and some groups are treated harshly.',
      'Hidden information makes it hard to trust the numbers.',
      'Heavy industry adds a lot of pollution.',
    ],
    affirms: ['common_good', 'poor'],
    challenges: ['religious_freedom', 'participation'],
  },
  vellmark: {
    inOwnWords:
      'We believe a society is strongest when everyone is cared for. We pay high taxes for health care, schools and child care for all. Our market is open and mostly private, and unions and employers bargain together. We trust each other, and we work closely with other nations, especially on the climate.',
    strengths: [
      'Universal services: everyone gets health care and good schools.',
      'High trust: people and leaders work together.',
      'Fair bargaining keeps wages fair and strikes rare.',
      'Climate leadership through clean energy and careful planning.',
    ],
    critics: [
      'High taxes can slow business growth.',
      'An aging population makes services expensive.',
      'A small military depends on allies.',
      'Big programs can be hard to change.',
    ],
    affirms: ['solidarity', 'creation'],
    challenges: ['subsidiarity', 'initiative'],
  },
  alvera: {
    inOwnWords:
      'We believe every person has dignity. Decisions should be made at the most local level that works: families, towns and regions first. A social market economy mixes free enterprise with protections for workers and families. We stand by our neighbors and care for the natural world.',
    strengths: [
      'Local decisions fit local needs.',
      'Worker protections and support for families.',
      'Solidarity: we help other nations in need.',
      'Care for the land and the sea.',
    ],
    critics: [
      'Coalitions can be slow and settle for weak compromises.',
      'Protections and benefits cost money and can slow growth.',
      'Helping others costs resources at home.',
      'An aging workforce strains the economy.',
    ],
    affirms: ['subsidiarity', 'solidarity'],
    challenges: ['poor', 'workers'],
  },
  karsova: {
    inOwnWords:
      'We believe a nation belongs to its people. We protect our heritage, our borders, and our farms and factories. We want to grow our own food and make our own goods so no one can push us around. The voice of ordinary citizens matters more than far-away institutions.',
    strengths: [
      'Self-reliance: we can feed ourselves in hard times.',
      'Secure borders and a strong army.',
      'Strong communities and traditions give people roots.',
      'Leaders listen to the people through referendums.',
    ],
    critics: [
      'Tariffs raise prices and shrink trade.',
      'Going it alone can mean less help in a crisis.',
      'Turning newcomers away can leave jobs unfilled and families without refuge.',
      'A very strong leader may face few checks.',
    ],
    affirms: ['homeland', 'family'],
    challenges: ['welcome', 'solidarity'],
  },
  aurem: {
    inOwnWords:
      'We believe stability lets a nation plan for the long run. Our Crown and council guide the country with a vision for the future. We use our oil wealth wisely, saving in a national fund and building new industries before the oil runs out. Change should come step by step.',
    strengths: [
      'Stability and continuity.',
      'A long-term vision and a national savings fund.',
      'Fast decisions for big projects.',
      'Energy wealth shared through public payments.',
    ],
    critics: [
      'Citizens do not vote for their leaders.',
      'Loyalty payments can hide unhappiness.',
      'Big projects do not always deliver.',
      'Oil and gas add to warming.',
    ],
    affirms: ['common_good', 'family'],
    challenges: ['participation', 'creation'],
  },
};

/** Builds a Steelman card. The lens is included only when `withLens` is true (debrief). */
export function steelmanCard(country: CountryId, withLens: boolean): SteelmanCard {
  const s = SOURCES[country];
  return {
    inOwnWords: s.inOwnWords,
    strengths: s.strengths.slice(),
    critics: s.critics.slice(),
    lens: withLens
      ? { affirms: s.affirms.map((p) => LENS_PRINCIPLES[p]), challenges: s.challenges.map((p) => LENS_PRINCIPLES[p]) }
      : null,
  };
}

export interface RoleGuide {
  role: RoleId;
  title: string;
  summary: string;
  duties: string[];
  tips: string[];
}

export const ROLE_GUIDES: Record<RoleId, RoleGuide> = {
  head: {
    role: 'head',
    title: 'Head of State',
    summary: 'You lead the cabinet, set the strategy and sign the final orders each turn.',
    duties: [
      'Run the cabinet meeting and agree on 3 priorities.',
      'Sign the team’s orders before the deadline.',
      'Declare war (only if you must) and use your country’s unique ability.',
      'Ratify treaties that the Foreign Minister negotiates.',
    ],
    tips: [
      'Ask every minister what their private briefing says. You only see part of the picture.',
      'Check your decision rule. Some countries need two or more signatures.',
      'If you are absent, any minister present may sign for you.',
    ],
  },
  foreign: {
    role: 'foreign',
    title: 'Foreign Minister and UN Ambassador',
    summary: 'You speak for your nation to the world: treaties, embassies and the World Assembly.',
    duties: [
      'Negotiate treaties and embassy exchanges.',
      'Cast your nation’s vote at the World Assembly (Turns 5, 9, 13, 17).',
      'Announce your nation’s climate pledge at each Assembly.',
      'Propose motions at the Assembly.',
    ],
    tips: [
      'An embassy lets both nations see each other’s core numbers.',
      'Keep your promises: broken treaties and pledges are public.',
      'Speak in character: "Our government believes..."',
    ],
  },
  defense: {
    role: 'defense',
    title: 'Defense Minister',
    summary: 'You keep the nation safe: build units, set the military posture and plan any attack.',
    duties: [
      'Build Army, Navy, Air Wing and Defenses (they are ready next turn).',
      'Set your posture: Defensive, Ready or Alert.',
      'Plan attacks only after the Head of State declares war.',
    ],
    tips: [
      'Only the Army can take and hold land. Islands need a Navy to reach.',
      'Defenders get +20%, and mountains and islands add +10%.',
      'Every attack costs your nation Public Support. Check the odds first.',
    ],
  },
  finance: {
    role: 'finance',
    title: 'Finance and Trade Minister',
    summary: 'You run the budget, the trades, the World Market and any tariffs or embargoes.',
    duties: [
      'Keep Treasury above 3 to avoid a credit downgrade.',
      'Trade Energy, Food and Treasury with other nations.',
      'Sell extra Energy or buy what you lack on the World Market.',
      'Invest in economic development.',
    ],
    tips: [
      'Resources you offer in a trade are held in escrow until the offer closes.',
      'Watch upkeep: every building and unit costs money each turn.',
      'Tariffs bring in money but make every deal with that nation smaller.',
    ],
  },
  intel: {
    role: 'intel',
    title: 'Intelligence Chief',
    summary: 'You run secret missions: gather information, protect your country and more.',
    duties: [
      'Run up to 2 missions per turn.',
      'Share what you learn with your cabinet.',
      'Protect your nation with counterintelligence.',
    ],
    tips: [
      'Gathering intelligence works 90% of the time; stealing technology only 20%.',
      'Caught spies are named in the news, and your nation loses Public Support.',
      'Some nations have strong counterintelligence. Check the odds.',
    ],
  },
  interior: {
    role: 'interior',
    title: 'Interior Minister',
    summary: 'You care for the people at home: Public Support, programs, climate action and crisis response.',
    duties: [
      'Build domestic programs like health, schools and farms.',
      'Take one climate action per turn (cut Carbon or adapt).',
      'Respond to crises to protect your people.',
      'Watch your factions and keep Public Support healthy.',
    ],
    tips: [
      'Each faction wants something. The faction mood line shows who is happy.',
      'Resilience makes every climate hit smaller.',
      'Up to half of a program’s cost can be paid in Energy.',
    ],
  },
};

/** Role merges by team size (Interior folds into Head of State on teams of 3-5). */
export const ROLE_MERGES: Record<number, RoleId[][]> = {
  1: [['head', 'foreign', 'defense', 'finance', 'intel', 'interior']],
  2: [['head', 'finance', 'interior'], ['foreign', 'defense', 'intel']],
  3: [['head', 'finance', 'interior'], ['foreign'], ['defense', 'intel']],
  4: [['head', 'interior'], ['foreign'], ['defense', 'intel'], ['finance']],
  5: [['head', 'interior'], ['foreign'], ['defense'], ['finance'], ['intel']],
  6: [['head'], ['foreign'], ['defense'], ['finance'], ['intel'], ['interior']],
};
